import { Table, Form, Col, Dropdown, Modal, Button } from "react-bootstrap";
import { Eye, MoreVertical, ChevronRight } from "lucide-react";
import { FaSearch } from 'react-icons/fa';
import { useState, useEffect, forwardRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ResultsPagination from "../components/ResultsPagination";
import axios from "axios";
import { toast } from "react-toastify";
import "../styles/Tasklist.css";

function TaskList() {
  const { role, user } = useAuth();
  const navigate = useNavigate();
  const isOperations = role === "operations";
  const isAdmin = role === "admin";
  const isStaff = isOperations || isAdmin;

  const [tasks, setTasks] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [reasons, setReasons] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [deadlineFilter, setDeadlineFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [uniqueEmployees, setUniqueEmployees] = useState([]);
  const [cards, setCards] = useState([]);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [stats, setStats] = useState({ total: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [fetchingFilesFor, setFetchingFilesFor] = useState(null);
  const [filesStats, setFilesStats] = useState({ total: 0, current: 0, shared: 0 });

  const CustomToggle = forwardRef(({ children, onClick }, ref) => (
    <div
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
    >
      {children}
    </div>
  ));

  const handleView = (task) => {
    const tenderIdentifier = task.tender_id_display || task.tender_number || task.tenderId || task.id;
    navigate(`/tender/${tenderIdentifier}`, { state: { task, sourceTab: "taskList" } });
  };

  const handleEdit = (task) => {
    const tenderIdentifier = task.tender_id_display || task.tender_number || task.tenderId || task.id;
    navigate(`/tender/${tenderIdentifier}`, {
      state: { task, sourceTab: "taskList", edit: true },
    });
  };

  const handlePerPageChange = (val) => {
    setPerPage(Number(val));
    setActivePage(1);
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return "/images/pdfimage.svg";
    const ext = fileName.toLowerCase();
    if (ext.includes('.pdf')) return "/images/pdfimage.svg";
    if (ext.includes('.doc') || ext.includes('.docx')) return "/images/wordimage.svg";
    if (ext.includes('.xls') || ext.includes('.xlsx')) return "/images/excelimage.svg";
    if (ext.includes('.jpg') || ext.includes('.jpeg') || ext.includes('.png')) return "/images/imageicon.svg";
    if (ext.includes('.zip') || ext.includes('.rar')) return "/images/archiveicon.svg";
    return "/images/pdfimage.svg";
  };

  const formatDate = (date) => {
    if (!date || date === "—" || date === "") return "—";
    if (typeof date === "string" && (date.includes(",") || (date.split(".").length - 1) >= 2)) return "—";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(value) || Number(value) <= 0) return "N/A";
    const num = Number(value);
    if (num < 1000) return "N/A";
    if (num >= 100000 && num < 10000000) {
      const lakhs = (num / 100000).toFixed(2);
      const formatted = parseFloat(lakhs) === parseInt(lakhs) ? parseInt(lakhs) : lakhs;
      return `₹${formatted} Lakh`;
    }
    if (num >= 10000000) {
      const crores = (num / 10000000).toFixed(2);
      const formatted = parseFloat(crores) === parseInt(crores) ? parseInt(crores) : crores;
      return `₹${formatted} Cr`;
    }
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/tender-stats`, {
        params: { userId: isStaff ? null : user?.id, isAdmin: isStaff },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [isStaff, user?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

const fetchTasks = useCallback(async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem("token");
    
    const params = {
      userId: user?.id,
      page: activePage,
      limit: perPage,
      status: statusFilter,
      priority: priorityFilter,
      deadline: deadlineFilter,
      customerFilter: employeeFilter,
      activeTab: ""
    };
    
    if (debouncedSearch && debouncedSearch.trim().length >= 2) {
      params.search = debouncedSearch.trim();
    }
    
    const res = await axios.get(
      `${process.env.REACT_APP_BASE_API_URL}/api/tender/tender-list`,
      {
        params: params,
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const applications = res.data.applications || [];
    
    const tasksWithData = applications.map((task) => ({
      ...task,
      documentCount: task.document_count || 0,
      sharedCount: task.shared_count || 0,
      currentDocCount: task.current_doc_count || 0,
      documents: [],
      formattedValue: formatCurrency(task.value)
    }));

    setTasks(tasksWithData);
    setTotalPages(res.data.totalPages || 1);

    if (res.data.globalCustomers && isStaff) {
      setUniqueEmployees(res.data.globalCustomers);
    }
  } catch (err) {
    if (err.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    console.error("Fetch tasks error:", err);
  } finally {
    setLoading(false);
  }
}, [user?.id, isStaff, activePage, perPage, debouncedSearch, statusFilter, priorityFilter, deadlineFilter, employeeFilter]);
  const handlePriorityChange = async (id, priority) => {
    if (!priority) return;
    try {
      const token = localStorage.getItem("token");
      setTasks(prev => prev.map(task => task.id === id ? { ...task, priority } : task));
      await axios.put(`${process.env.REACT_APP_BASE_API_URL}/api/tender-applications/${id}`, { priority }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update priority");
    }
  };

  const updateStatus = async (id, status, rejectionReason = null) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BASE_API_URL}/api/tender-applications/${id}`, { status, rejectionReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(prev => prev.map(task =>
        task.id === id ? { ...task, status, rejection_reason: rejectionReason, tempReject: false } : task
      ));
      fetchStats();
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed");
    }
  };

  const handleStatusChange = (id, status) => {
    const task = tasks.find(t => t.id === id);
    if (!task || task.tempReject) return;
    if (role === "customer" && status !== "rejected") return;
    if (status === "rejected") {
      setTasks(prev => prev.map(t => t.id === id ? { ...t, tempReject: true } : t));
      return;
    }
    updateStatus(id, status);
  };

  const handleRejectConfirm = (id, reason) => {
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }
    updateStatus(id, "rejected", reason);
  };

  const handleCardClick = (card) => {
    if (card.label === "Total Tenders") setStatusFilter("");
    else if (card.label === "Client Approved") setStatusFilter("approved");
    else if (card.label === "Client Rejected") setStatusFilter("rejected");
    setActivePage(1);
  };

  const handleResetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPriorityFilter("");
    setDeadlineFilter("");
    setEmployeeFilter("");
    setActivePage(1);
  };

  const handleViewFile = (file) => {
    const fileId = file.id || file.document_id;
    if (!fileId) {
      toast.error("File ID not available");
      return;
    }
    const url = `${process.env.REACT_APP_BASE_API_URL}/api/file/view/${fileId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadFile = (file) => {
    const fileId = file.id || file.document_id;
    if (!fileId) {
      toast.error("File ID not available");
      return;
    }

    const toastId = toast.loading(`Downloading ${file.file_name || 'document'}...`);
    const url = `${process.env.REACT_APP_BASE_API_URL}/api/file/download/${fileId}`;

    fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem("token")}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Download failed');
        }
        return response.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.file_name || file.name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
        toast.update(toastId, { render: `Downloaded: ${file.file_name}`, type: "success", isLoading: false, autoClose: 3000 });
      })
      .catch(err => {
        console.error('Download error:', err);
        toast.update(toastId, { render: `Failed to download: ${file.file_name}`, type: "error", isLoading: false, autoClose: 3000 });
      });
  };

  const downloadAllFiles = () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.info("No files to download");
      return;
    }

    if (downloading) {
      toast.info("Download already in progress...");
      return;
    }

    setDownloading(true);
    const filesToDownload = [...selectedFiles];
    let completedCount = 0;
    let successCount = 0;
    let failCount = 0;

    toast.info(`Downloading ${filesToDownload.length} files...`);

    const checkCompletion = () => {
      if (completedCount === filesToDownload.length) {
        setDownloading(false);
        if (failCount === 0) {
          toast.success(`All ${successCount} files downloaded successfully!`);
        } else {
          toast.warning(`Downloaded ${successCount}/${filesToDownload.length} files. ${failCount} failed.`);
        }
      }
    };

    filesToDownload.forEach((file, index) => {
      const fileId = file.id || file.document_id;

      if (!fileId) {
        completedCount++;
        failCount++;
        checkCompletion();
        return;
      }

      const url = `${process.env.REACT_APP_BASE_API_URL}/api/file/download/${fileId}?t=${Date.now()}&idx=${index}`;

      setTimeout(() => {
        fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`
          }
        })
          .then(response => {
            if (!response.ok) throw new Error('Download failed');
            return response.blob();
          })
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.file_name || file.name || `document-${index + 1}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
            successCount++;
          })
          .catch(err => {
            console.error('Download error:', err);
            failCount++;
          })
          .finally(() => {
            completedCount++;
            checkCompletion();
          });
      }, index * 300);
    });
  };

const handleShowFiles = async (task, event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  setModalPosition({
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX
  });

  setFetchingFilesFor(task.id);
  
  try {
    const token = localStorage.getItem("token");
    
    const docRes = await axios.get(
      `${process.env.REACT_APP_BASE_API_URL}/api/application-documents/${task.id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const docs = docRes.data.documents || [];
    const stats = docRes.data.stats || { total: 0, current: 0, shared: 0 };
    
    setSelectedFiles(docs);
    setFilesStats(stats);
    
    if (docs.length > 0) {
      const sharedCount = docs.filter(d => d.is_shared).length;
      const currentCount = docs.filter(d => d.is_for_current_tender).length;
      // toast.info(`📄 ${docs.length} documents (${currentCount} for this tender, ${sharedCount} shared)`);
    } else {
      toast.info("No documents uploaded yet");
    }
    
  } catch (err) {
    console.error('Error fetching documents:', err);
    setSelectedFiles([]);
    setFilesStats({ total: 0, current: 0, shared: 0 });
    toast.error("Failed to fetch documents");
  } finally {
    setFetchingFilesFor(null);
  }
  setShowFilesModal(true);
};
  const handleCloseFiles = () => {
    setShowFilesModal(false);
    setDownloading(false);
    setSelectedFiles([]);
    setFilesStats({ total: 0, current: 0, shared: 0 });
  };

  const triggerDelete = (id) => {
    setTaskToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BASE_API_URL}/api/tender-applications/${taskToDelete}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTasks();
      fetchStats();
      toast.success("Task deleted successfully!");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete the task.");
    } finally {
      setShowDeleteModal(false);
      setTaskToDelete(null);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTasks();
  }, [fetchStats, fetchTasks]);

  useEffect(() => {
    setCards([
      { id: 1, count: stats.total, label: "Total Tenders", Image: "/images/New tenders.svg", bg: "bg-blue-100" },
      { id: 2, count: stats.approved, label: "Client Approved", Image: "/images/Client approved.svg", bg: "bg-green-100" },
      { id: 3, count: stats.rejected, label: "Client Rejected", Image: "/images/Clarification.svg", bg: "bg-red-100" }
    ]);
  }, [stats]);

  useEffect(() => {
    const interval = setInterval(() => { fetchTasks(); }, 60000);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchTasks();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchTasks]);

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/rejection-reasons`);
        setReasons(res.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchReasons();
  }, []);

  useEffect(() => {
    if (localStorage.getItem("refreshTasks") === "true") {
      fetchTasks();
      fetchStats();
      localStorage.removeItem("refreshTasks");
    }
  }, [fetchTasks, fetchStats]);

  return (
    <>
      <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "''" }}>
        <ol className="breadcrumb d-flex align-items-center mb-0" style={{ fontSize: "14px" }}>
          <li className="breadcrumb-item d-flex align-items-center">
            <Link to="/" className="text-decoration-none text-secondary">Home</Link>
            <ChevronRight size={16} className="mx-1 text-secondary" />
          </li>
          <li className="breadcrumb-item fw-semibold" style={{ color: "#2F7A87" }}>Task List</li>
        </ol>
      </nav>

      <div className="task-container container-fluid">
        <div className="task-header mb-4">
          <div className="d-flex align-items-center">
            <h4 className="task-title">Task List</h4>
          </div>
          <span className="task-subtitle">View assigned queries, track leads, and manage sales activities in one place.</span>
        </div>

        {isStaff && (
          <div className="row mb-4 g-3">
            {cards.map((card) => (
              <div key={card.id} className="col-xl-3 col-lg-4 col-md-6 mb-1">
                <div
                  className={`dashboard-card d-flex justify-content-between align-items-center p-3 shadow-sm border rounded bg-white ${((card.label === "Client Approved" && statusFilter === "approved") ||
                    (card.label === "Client Rejected" && statusFilter === "rejected") ||
                    (card.label === "Total Tenders" && statusFilter === ""))
                    ? "active-card"
                    : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleCardClick(card)}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle ${card.bg}`}>
                      <img src={card.Image} alt={card.label} className="card-icon-img" style={{ border: 'none', background: 'transparent' }} />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold">{card.count}</h5>
                      <small className="text-muted">{card.label}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="shadow-sm pt-3 bg-white rounded border task-main-wrapper" style={{ width: "100%" }}>

          <div className="row g-3 mb-4 align-items-center mx-0">
            <Col md={3}>
              <Form.Label className="text-cons d-block" style={{ visibility: "hidden" }}>Search</Form.Label>
              <div className="search-box">
                <FaSearch className="search-start-icon" />
                <input
                  type="search"
                  className="form-control search-input"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="search-end-btn"><FaSearch /></button>
              </div>
            </Col>

            {isStaff && (
              <div className="col-md-2 ps-5">
                <Form.Label className="text-cons">Customer</Form.Label>
                <Form.Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} style={{ borderRadius: "50px", width: "170px" }}>
                  <option value="">All </option>
                  {uniqueEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </Form.Select>
              </div>
            )}

            <div className="col-md-2 ps-5">
              <Form.Label className="text-cons">Tender Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ borderRadius: "50px", width: "170px" }}>
                <option value="">All </option>
                <option value="pending">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Client Rejected</option>
              </Form.Select>
            </div>

            <div className="col-md-2 ps-5">
              <Form.Label className="text-cons">Priority</Form.Label>
              <Form.Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="custom-select-radius" style={{ width: "170px" }}>
                <option value="">All </option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Form.Select>
            </div>

            <div className="col-md-3 ps-5">
              <Form.Label className="text-cons">Submission Deadline</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Select value={deadlineFilter} onChange={(e) => setDeadlineFilter(e.target.value)} className="custom-select-radius" style={{ width: "170px" }}>
                  <option value="">All </option>
                  <option value="7days">In one week</option>
                  <option value="30days">In one month</option>
                </Form.Select>
                <div onClick={handleResetFilters} className="d-flex align-items-center gap-2" style={{ cursor: "pointer", whiteSpace: "nowrap", color: "#333" }} title="Reset Filters">
                  <img src="/images/filter.svg" alt="Reset" style={{ width: '16px', height: '16px' }} />
                  <span style={{ fontWeight: 400, fontSize: "14px" }}>Reset Filters</span>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive" style={{
            width: "100%",
            overflowX: "auto",
            border: "1px solid rgba(229, 231, 235, 1)",
            borderRadius: "12px",
            background: "#fff"
          }}>
            <Table hover className="task-table align-middle small mb-0" style={{
              width: "100%",
              minWidth: isAdmin ? "85rem" : isStaff ? "90rem" : "80rem",
              tableLayout: "fixed"
            }}>
              <thead style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                <tr className="text-muted" style={{ fontWeight: 500 }}>
                  {isAdmin && <th style={{ width: "5%", padding: "10px 8px", border: "none" }}>Operative</th>}
                  {isStaff && <th style={{ width: "7%", padding: "10px 8px", border: "none" }}>Customer</th>}
                  <th style={{ width: "10%", padding: "10px 8px", border: "none" }}>Tender Title</th>
                  <th style={{ width: "9%", padding: "10px 8px", border: "none" }}>Organization</th>
                  <th style={{ width: "6%", padding: "10px 8px", border: "none" }}>Ministry</th>
                  <th style={{ width: "6%", padding: "10px 8px", border: "none" }}>State</th>
                  <th style={{ width: "7%", padding: "10px 8px", border: "none" }}>Deadline</th>
                  <th style={{ width: "6%", padding: "10px 8px", border: "none" }}>Tender Value</th>
                  <th style={{ width: "10%", padding: "10px 8px", border: "none" }} className="text-center">Status</th>
                  <th style={{ width: "8%", padding: "10px 8px", border: "none" }} className="text-center">Reason</th>
                  <th style={{ width: "8%", padding: "10px 8px", border: "none" }}>Priority</th>
                  <th style={{ width: "6%", padding: "10px 8px", border: "none" }}>Date</th>
                  <th className="text-center" style={{ width: "8%", padding: "10px 8px", border: "none" }}>Documents</th>
                  <th className="text-center" style={{ width: "7%", padding: "10px 8px", border: "none" }}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: "none", padding: "10px 8px" }}>
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="text-center py-4 text-muted">
                      {loading ? "Loading..." : "No tasks found"}
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task.id} style={{ borderBottom: "1px solid rgba(229, 231, 235, 1)" }}>
                      {isAdmin && (
                        <td style={{ border: "none", padding: "10px 8px" }}>
                          <div className="text-truncate text-dark" title={task.operative}>{task.operative || "—"}</div>
                        </td>
                      )}
                      {isStaff && (
                        <td style={{ border: "none", padding: "10px 8px" }}>
                          <div className="text-truncate text-dark" title={task.customer}>{task.customer || "—"}</div>
                        </td>
                      )}
                      <td style={{ border: "none", padding: "10px 8px" }}>
                        <div className="text-truncate fw-semibold text-dark" title={task.title}>{task.title || "—"}</div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }}>
                        <div className="text-truncate text-muted" title={task.org}>{task.org || "—"}</div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }}>
                        <div className="text-truncate text-muted" title={task.ministry}>{task.ministry || "—"}</div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }}>
                        <div className="text-truncate text-muted" title={task.state}>{task.state || "—"}</div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }}>
                        <div className="text-truncate text-muted" title={formatDate(task.deadline)}>{formatDate(task.deadline)}</div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }}>
                        <div className="text-truncate text-dark fw-medium" title={task.value}>
                          {task.formattedValue || formatCurrency(task.value)}
                        </div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }} className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2" style={{ width: "100%" }}>
                          <Form.Select
                            value={task.status || "pending"}
                            onChange={(e) => handleStatusChange(task.id, e.target.value)}
                            className={`status-dropdown custom-status-select ${task.status === "approved" ? "status-approved" : task.status === "rejected" ? "status-rejected" : "status-pending"}`}
                            style={{ minWidth: "140px", maxWidth: "155px", borderRadius: "50px", fontSize: "12px", padding: "4px 8px" }}
                          >
                            <option value="pending">Pending Approval</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Client Rejected</option>
                          </Form.Select>
                        </div>
                      </td>
                      <td style={{ border: "none", padding: "10px 8px" }} className="text-center">
                        <div className="d-flex align-items-center justify-content-center">
                          {(task.tempReject || task.status === "rejected") ? (
                            <Form.Select
                              className="reason-dropdown"
                              style={{ minWidth: "90px", maxWidth: "100px", fontSize: "12px", borderRadius: "50px", padding: "4px 8px" }}
                              value={task.rejection_reason || ""}
                              disabled={task.status === "rejected" && task.rejection_reason}
                              onChange={(e) => handleRejectConfirm(task.id, e.target.value)}
                              title={task.rejection_reason || ""}
                            >
                              <option value="" disabled>Reason</option>
                              {reasons.map((r, idx) => <option key={idx} value={r}>{r}</option>)}
                            </Form.Select>
                          ) : (
                            <span className="text-muted" style={{ fontSize: "13px" }}>—</span>
                          )}
                        </div>
                      </td>
                      <td style={{ border: "none" }}>
                        <Form.Select
                          value={task.priority ?? ""}
                          onChange={(e) => handlePriorityChange(task.id, e.target.value)}
                          disabled={!isStaff}
                          className={`priority-dropdown custom-priority-select ${task.priority === "High" ? "priority-high" : task.priority === "Medium" ? "priority-medium" : task.priority === "Low" ? "priority-low" : ""}`}
                          style={{ minWidth: "90px", maxWidth: "110px", borderRadius: "50px", fontSize: "12px", padding: "4px 8px" }}
                        >
                          <option value="">Select</option>
                          <option value="High" className="priority-high">High</option>
                          <option value="Medium" className="priority-medium">Medium</option>
                          <option value="Low" className="priority-low">Low</option>
                        </Form.Select>
                      </td>
                      <td style={{ border: "none" }}>
                        <div className="text-truncate text-muted" title={formatDate(task.created_at)}>{formatDate(task.created_at)}</div>
                      </td>
                      <td className="text-center" style={{ border: "none", verticalAlign: "middle" }}>
                        <button
                          onClick={(e) => handleShowFiles(task, e)}
                          disabled={fetchingFilesFor === task.id}
                          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1"
                        >
                          {fetchingFilesFor === task.id ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: "12px", height: "12px", color: '#2F7A87', borderWidth: '0.15em' }}></span>
                              <span style={{ color: '#2F7A87', fontSize: "13px" }}>Loading...</span>
                            </>
                          ) : (
                            <>
                              <img src="/images/link.svg" alt="link" style={{ width: "16px" }} />
                              <span style={{ fontSize: "13px" }}>
                                {task.documentCount > 0 ? `${task.documentCount} file(s)` : "0 files"}
                                {task.sharedCount > 0 && (
                                  <span className="badge bg-info ms-1" style={{ fontSize: "9px", padding: "2px 6px" }}>
                                  
                                  </span>
                                )}
                              </span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="text-center" style={{ border: "none" }}>
                        <div className="d-flex justify-content-center align-items-center gap-1">
                          <Eye size={18} className="view-icon" style={{ cursor: "pointer", color: "#64748b" }} onClick={() => handleView(task)} />
                          <Dropdown align="end">
                            <Dropdown.Toggle as={CustomToggle}>
                              <MoreVertical size={18} style={{ color: "#64748b", cursor: "pointer" }} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                              {/* <Dropdown.Item onClick={() => handleEdit(task)}>Edit</Dropdown.Item> */}
                              <Dropdown.Item onClick={() => triggerDelete(task.id)} className="text-danger">Delete</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {showFilesModal && (
            <div className="custom-files-modal border rounded" style={{
              position: "fixed",
              top: Math.min(modalPosition.top - 10, window.innerHeight - 350),
              left: Math.min(modalPosition.left - 175, window.innerWidth - 400),
              zIndex: 9999,
              minWidth: "350px",
              maxWidth: "500px",
              background: "white",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)"
            }}>
              <div className="modal-content shadow">
                <div className="modal-header py-2 px-3 d-flex justify-content-between align-items-center border-bottom">
                  <h6 className="mb-0 fw-semibold">
                    <i className="bi bi-files me-2"></i>
                    {selectedFiles.length} Document(s)
                    {filesStats.shared > 0 && (
                      <span className="badge bg-info ms-2" style={{ fontSize: "10px" }}>
                        🔗 {filesStats.shared} shared across tenders
                      </span>
                    )}
                    {filesStats.current > 0 && filesStats.shared > 0 && (
                      <span className="badge bg-success ms-1" style={{ fontSize: "10px" }}>
                        📌 {filesStats.current} for this tender
                      </span>
                    )}
                  </h6>
                  <button className="btn-close" onClick={handleCloseFiles}></button>
                </div>
                <div className="modal-body p-0" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {selectedFiles.length > 0 ? (
                    selectedFiles.map((file, idx) => (
                      <div key={idx} className={`d-flex justify-content-between align-items-center px-3 py-2 ${idx !== selectedFiles.length - 1 ? "border-bottom" : ""}`}>
                        <div className="d-flex align-items-center gap-2" style={{ maxWidth: "200px" }}>
                          <img
                            src={getFileIcon(file.file_name)}
                            alt="file"
                            style={{ width: "24px", height: "24px" }}
                            onError={(e) => { e.target.src = "/images/pdfimage.svg"; }}
                          />
                          <span className="text-truncate" style={{ fontSize: "13px" }} title={file.file_name}>
                            {file.file_name || "Unknown"}
                          </span>
                          {file.is_shared && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: "9px" }}>
                              🔗 Shared
                            </span>
                          )}
                          {file.tender_count > 1 && (
                            <span className="badge bg-light text-muted" style={{ fontSize: "9px" }}>
                              {file.tender_count} tenders
                            </span>
                          )}
                          {file.is_for_current_tender && (
                            <span className="badge bg-success" style={{ fontSize: "9px" }}>
                              Current
                            </span>
                          )}
                        </div>
                        <div className="d-flex gap-3">
                          <i
                            className="bi bi-eye icon-eyes"
                            style={{ cursor: "pointer", color: "#2F7A87", fontSize: "16px" }}
                            onClick={(e) => { e.stopPropagation(); handleViewFile(file); }}
                            title="View Document"
                          ></i>
                          <i
                            className="bi bi-download icon-eyes"
                            style={{ cursor: "pointer", color: "#2F7A87", fontSize: "16px" }}
                            onClick={(e) => { e.stopPropagation(); handleDownloadFile(file); }}
                            title="Download Document"
                          ></i>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted text-center py-4">
                      <i className="bi bi-file-earmark-text" style={{ fontSize: "24px" }}></i>
                      <p className="mb-0 mt-2">No documents uploaded</p>
                      <p className="text-muted small">Documents uploaded for any tender will appear here</p>
                    </div>
                  )}
                </div>
                <div className="modal-footer py-2 px-3 d-flex justify-content-between align-items-center border-top">
                  <span className="text-muted small">
                    {selectedFiles.length > 0 && `${selectedFiles.length} file(s)`}
                    {filesStats.shared > 0 && (
                      <span className="ms-2 text-info">
                        ({filesStats.shared} shared)
                      </span>
                    )}
                  </span>
                  <button
                    className="btn btn-sm btn-primary rounded-pill px-3 d-flex align-items-center gap-2"
                    onClick={downloadAllFiles}
                    disabled={downloading}
                    style={{ background: downloading ? "#ccc" : "#2F7A87", border: "none" }}
                  >
                    <i className="bi bi-download"></i> {downloading ? "Downloading..." : "Download All"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" animation={true}>
            <Modal.Header closeButton className="border-0 pb-0">
              <Modal.Title className="fw-bold" style={{ color: "#dc2626", fontSize: "1.25rem" }}>Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-muted pt-2 pb-4 px-4" style={{ fontSize: "15px" }}>
              Are you sure you want to delete this task? This action cannot be undone.
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 px-4 pb-4">
              <Button variant="light" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4 fw-medium shadow-sm border" style={{ fontSize: "14px" }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete} className="rounded-pill px-4 fw-medium shadow-sm border-0" style={{ fontSize: "14px", backgroundColor: "#dc2626" }}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>

          <div className="table-footer gap-3 p-3 mt-3 d-flex align-items-center justify-content-between border-top">
            <span className="text-muted small">
              Showing {(activePage - 1) * perPage + 1}–{Math.min((activePage - 1) * perPage + tasks.length, activePage * perPage)}
            </span>
            <ResultsPagination
              activePage={activePage}
              setActivePage={setActivePage}
              perPage={perPage}
              setPerPage={handlePerPageChange}
              totalPages={totalPages}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default TaskList;